import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import './App.css';
import translations from './translations.js';
import logo from './assets/fuso-logo.svg';
import chevronRight from './assets/chevron-right.svg';
import FeedbackLink from "./feedbackButton/feedbackButton";
import ProfilePhoto from "./profilePhoto/profilePhoto.js";
import AdminButton from "./admin/AdminButton.js";
import LanguageDropDown from "./languageDropDown/dropDown.js";
import ModelDropDown from "./modelDropDown/modelDropDown.js";
import YearDropDown from "./modelDropDown/yearDropDown.js";
import ResponseModeToggle from "./responseMode/ResponseModeToggle.js";
import { Tooltip, ChakraProvider, useToast } from '@chakra-ui/react';
import UserHistory from "./userHistory/userHistory.js";
import { Modal, ModalOverlay, ModalContent, ModalFooter, ModalBody, ModalCloseButton, useDisclosure, Button, Textarea } from '@chakra-ui/react'
import NewChat from "./assets/newChatHR.svg";
import toggleBtn from "./assets/toggleBtn.svg";
import botImgDTC from "./assets/mainChatImg.svg";
import redVector from "./assets/redVector.svg";
import StarRating from "./starRating/starRating";
import ProductTour from "./productTour/ProductTour";
import ChatView from "./chat/components/ChatView";
import ThemeToggle from "./chat/components/ThemeToggle";
import MemoryGraphButton from "./chat/components/MemoryGraph";
import KnowledgeButton from "./chat/components/KnowledgePanel";
import MyFilesButton from "./chat/components/MyFilesPanel";
import useChatStream from "./chat/hooks/useChatStream";
import { API_BASE, apiUrl } from "./chat/api/config";
import { fetchConversation } from "./chat/api/history";

const SERVER_URL = `${API_BASE}/`;
const FEEDBACK_SERVER_URL = SERVER_URL + 'feedback';
const VEHICLE_DATA_URL = SERVER_URL + 'vehicle-data';
const MAX_QUESTION_LENGTH = 2000;

// Helper function to detect iPad and mobile devices
const isMobileOrTablet = () => {
  const userAgent = navigator.userAgent || navigator.vendor || window.opera;
  // Check for iPad (including new iPads that identify as Mac)
  const isIPadDevice = /iPad/.test(userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  // Check for mobile devices
  const isMobile = /Android|webOS|iPhone|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent);
  // Check screen width for additional mobile detection
  const isSmallScreen = window.innerWidth <= 640;

  return isIPadDevice || isMobile || isSmallScreen;
};

const GUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

// Clear stale UUID or @tbdir.net values left in localStorage from old sessions
const storedUserinfo = localStorage.getItem('userinfo');
if (storedUserinfo && (GUID_RE.test(storedUserinfo) || storedUserinfo.includes('@tbdir.net'))) {
  localStorage.removeItem('userinfo');
}

const OverlayTwo = () => (
  <ModalOverlay
    bg='blackAlpha.300'
    backdropFilter='blur(10px) hue-rotate(90deg)'
  />
);

const EMPTY_HISTORY = { chatId: null, hasMore: false, nextBefore: null, loading: false, prepending: false };

function ConversationSkeleton() {
  return (
    <div className="cv-skeleton-chat" aria-hidden="true">
      {[0, 1].map((i) => (
        <React.Fragment key={i}>
          <span className="cv-skel cv-skel--user" />
          <span className="cv-skel cv-skel--line" style={{ width: '92%' }} />
          <span className="cv-skel cv-skel--line" style={{ width: '80%' }} />
          <span className="cv-skel cv-skel--line" style={{ width: '64%' }} />
        </React.Fragment>
      ))}
    </div>
  );
}

function App() {
  const [question, setQuestion] = useState('');
  const [selectedYear, setSelectedYear] = useState('');
  const [selectedModel, setSelectedModel] = useState('');  // Model dropdown state
  const [langOpt, setLangOpt] = useState(sessionStorage.getItem('langOpt') || 'Japanese');
  const [transcript, setTranscript] = useState('');
  const [sidebarVisible, setSidebarVisible] = useState(!isMobileOrTablet());
  const [yearsByModel, setYearsByModel] = useState({});  // Available manual years per model (from backend)
  const toast = useToast({ position: 'top-right' })
  const [childFunc, setChildFunc] = useState(null);
  const [removeChatFunc, setRemoveChatFunc] = useState([]);
  const { isOpen, onOpen, onClose } = useDisclosure()
  const { isOpen: isFeedbackOpen, onOpen: onFeedbackOpen, onClose: onFeedbackClose } = useDisclosure()
  const [overlay, setOverlay] = React.useState(<OverlayTwo />)
  const [feedback, setFeedback] = useState('');
  const [feedbackText, setFeedbackText] = useState('');
  const [feedbackRating, setFeedbackRating] = useState(5);
  const [feedbackSubmitted, setFeedbackSubmitted] = useState(false);
  const [voteFeedbackSubmitted, setVoteFeedbackSubmitted] = useState(false);
  const [responseVote, setResponseVote] = useState('');
  const [votes, setVotes] = useState({});  // message_id -> 'up' | 'down' (submitted)
  // message_id of the answer being rated in the feedback modal
  const [currentMessageId, setCurrentMessageId] = useState('');
  const [responseMode, setResponseMode] = useState('deep');
  const [historyPaging, setHistoryPaging] = useState(EMPTY_HISTORY);
  const [loadingConversation, setLoadingConversation] = useState(false);
  const conversationCache = useRef(new Map());
  const openSeq = useRef(0);

  useEffect(() => {
    sessionStorage.setItem('langOpt', langOpt);
  }, [langOpt]);

  // Fetch available manual years per model from backend (populates Year dropdown)
  useEffect(() => {
    fetch(VEHICLE_DATA_URL)
      .then((res) => res.json())
      .then((data) => setYearsByModel(data?.years_by_model || {}))
      .catch((err) => console.error('Failed to load vehicle year data:', err));
  }, []);

  // Auto-select the year when the chosen model has only one available year
  useEffect(() => {
    if (!selectedModel) return;
    const years = yearsByModel[selectedModel] || [];
    if (years.length === 1 && !selectedYear) {
      setSelectedYear(years[0]);
    }
  }, [selectedModel, yearsByModel]); // eslint-disable-line react-hooks/exhaustive-deps

  const translate = useCallback((text) => {
    if (!text || !translations[text]) return text;
    switch (langOpt) {
      case 'Japanese':
        return translations[text].translationJp || text;
      case 'Indonesian':
        return translations[text].translationIn || translations[text].translationEng || text;
      case 'Nepali':
        return translations[text].translationNp || translations[text].translationEng || text;
      case 'Sinhala':
        return translations[text].translationSl || translations[text].translationEng || text;
      case 'Vietnamese':
        return translations[text].translationVn || translations[text].translationEng || text;
      case 'Filipino':
        return translations[text].translationPh || translations[text].translationEng || text;
      case 'Bengali':
        return translations[text].translationBd || translations[text].translationEng || text;
      case 'English':
      default:
        return translations[text].translationEng || text;
    }
  }, [langOpt]);

  const triggerChildFunction = (entity) => {
    if (childFunc) childFunc(entity);
  };

  const resetSelectedChat = () => {
    if (typeof removeChatFunc === 'function') removeChatFunc("");
  };

  // ── Chat stream ───────────────────────────────────────────────────────────
  const chat = useChatStream({
    url: apiUrl('chat'),
    buildBody: (q) => ({
      question: q,
      lang_opt: langOpt,
      user_email: localStorage.getItem('userinfo') || "sumanth",
      model: selectedModel,
      year: selectedYear,
      response_mode: responseMode,
    }),
    onTurnDone: (turn) => {
      if (turn.conversationId) conversationCache.current.delete(turn.conversationId);
      // optimistic sidebar update: the conversation moves to (or appears at) the top
      triggerChildFunction({
        ChatId: turn.conversationId,
        UserResponse: turn.question,
        Timestamp: new Date().toISOString(),
        Model: selectedModel,
        Year: selectedYear,
        message_id: turn.messageId,
      });
    },
    onError: (event) => {
      toast({
        title: translate("Error"),
        description: event.message || translate("Unable to send your message. Please try again."),
        status: 'error',
        duration: 5000,
        isClosable: true
      });
    },
    messages: {
      network: translate('Connection lost. Please try again.'),
      timeout: translate('The server stopped responding. Please try again.'),
      http: translate('Unable to send your message. Please try again.'),
    },
  });
  const { reset: resetChat, loadHistory, prependHistory } = chat;
  const conversationId = chat.state.conversationId;

  const handleNewChat = (options) => {
    const keepSelection = Boolean(options && options.keepSelection === true);
    openSeq.current += 1;
    resetChat();
    resetSelectedChat();
    setHistoryPaging(EMPTY_HISTORY);
    setLoadingConversation(false);
    if (!keepSelection) {
      setSelectedModel('');  // Reset model selection
      setSelectedYear('');   // Reset year selection
    }
  };

  // ── History ───────────────────────────────────────────────────────────────
  const applyConversation = useCallback((result) => {
    loadHistory(result.messages, result.chatId, result.conversationInfo);
    setHistoryPaging({ chatId: result.chatId, hasMore: result.hasMore, nextBefore: result.nextBefore, loading: false, prepending: false });
  }, [loadHistory]);

  // Open a conversation from the sidebar: instantly from cache when we have it, else a skeleton until the
  // lightweight detail request (no base64 images) returns. The cache is refreshed in the background.
  const openConversation = useCallback(async (chatId) => {
    const seq = ++openSeq.current;
    const cached = conversationCache.current.get(chatId);
    if (cached) {
      applyConversation(cached);
    } else {
      loadHistory([], chatId, null);
      setHistoryPaging(EMPTY_HISTORY);
      setLoadingConversation(true);
    }
    try {
      const result = await fetchConversation(chatId, { lang: langOpt });
      conversationCache.current.set(chatId, result);
      if (seq !== openSeq.current) return;
      if (!cached || cached.rows.length !== result.rows.length) applyConversation(result);
    } catch (err) {
      if (seq === openSeq.current && !cached) {
        loadHistory([], '', null);
        throw err;
      }
    } finally {
      if (seq === openSeq.current) setLoadingConversation(false);
    }
  }, [applyConversation, loadHistory, langOpt]);

  const loadOlder = useCallback(async () => {
    const { chatId, nextBefore, loading } = historyPaging;
    if (!chatId || !nextBefore || loading) return;
    setHistoryPaging((h) => ({ ...h, loading: true }));
    try {
      const result = await fetchConversation(chatId, { before: nextBefore, lang: langOpt });
      setHistoryPaging((h) => ({ ...h, prepending: true }));
      prependHistory(result.messages);
      setHistoryPaging((h) => ({ ...h, hasMore: result.hasMore, nextBefore: result.nextBefore, loading: false }));
    } catch (err) {
      console.error('Error loading older messages:', err);
      setHistoryPaging((h) => ({ ...h, loading: false }));
    }
  }, [historyPaging, prependHistory, langOpt]);

  useEffect(() => {
    if (historyPaging.prepending) setHistoryPaging((h) => ({ ...h, prepending: false }));
  }, [chat.state.messages]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Sending ───────────────────────────────────────────────────────────────
  // A vehicle chip ("Which vehicle is this for?") or a "What I know" example sets the vehicle first; the question
  // is sent once the selectors hold it (buildBody reads them from the render).
  const [pendingAsk, setPendingAsk] = useState(null);
  const askWithVehicle = useCallback((model, year, text) => {
    setSelectedModel(model);
    setSelectedYear(year);
    setPendingAsk({ model, year, text });
  }, []);

  const handleSendMessage = (text, suggestion) => {
    if (chat.busy) return;

    if (suggestion && suggestion.action === 'set_vehicle') {
      askWithVehicle(suggestion.model, suggestion.year, suggestion.question || '');
      return;
    }
    // No vehicle chosen is fine: the backend answers small talk and memory questions directly, takes the vehicle
    // from the question or the conversation when it can, and otherwise asks for it in the chat.

    const currentQuestion = typeof text === 'string' ? text : question;
    if (!currentQuestion.trim()) return;

    if (currentQuestion.length > MAX_QUESTION_LENGTH) {
      toast({
        title: translate("Message too long"),
        description: translate("Questions must be 2000 characters or fewer."),
        status: 'error',
        duration: 5000,
        isClosable: true
      });
      return;
    }

    if (currentQuestion === question) setQuestion('');
    chat.send(currentQuestion, { mode: responseMode });
  };

  useEffect(() => {
    if (!pendingAsk || pendingAsk.model !== selectedModel || pendingAsk.year !== selectedYear) return;
    const { text } = pendingAsk;
    setPendingAsk(null);
    if (text && text.trim()) handleSendMessage(text);
  }, [pendingAsk, selectedModel, selectedYear]); // eslint-disable-line react-hooks/exhaustive-deps

  // The backend inferred the vehicle (named in the question, or this conversation's): show it in the selectors.
  const inferredVehicle = chat.state.vehicle;
  useEffect(() => {
    if (!inferredVehicle || selectedModel) return;
    setSelectedModel(inferredVehicle.model);
    setSelectedYear(inferredVehicle.year || '');
  }, [inferredVehicle]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Feedback ──────────────────────────────────────────────────────────────
  const handleVote = useCallback((message, vote) => {
    setOverlay(<OverlayTwo />);
    setResponseVote(vote);
    setCurrentMessageId(message.messageId || '');
    setTranscript(`**Assistant**: ${message.text}\n\n`);
    onOpen();
  }, [onOpen]);

  const handleFeedbackChange = (event) => {
    setFeedback(event.target.value);
  };

  const handleFeedbackTextChange = (event) => {
    setFeedbackText(event.target.value);
  };

  const handleSidebarFeedbackSubmit = async () => {
    if (!feedbackRating) return;

    try {
      let feedback_body = {
        UserEmail: localStorage.getItem('userinfo'),
        Rating: feedbackRating,
        Feedback: feedbackText,
      };

      const response = await axios.post(SERVER_URL + 'submit-feedback', feedback_body);

      if (response.data.valid) {
        setFeedbackSubmitted(true);
        setTimeout(() => {
          setFeedbackSubmitted(false);
          setFeedbackText('');
          setFeedbackRating(5);
          onFeedbackClose();
        }, 2000);
      } else {
        toast({
          title: translate('Submission failed'),
          description: translate('Something went wrong. Please try again.'),
          status: 'error',
          duration: 2000,
          isClosable: true,
        });
      }
    } catch (error) {
      toast({
        title: translate('Submission failed'),
        description: translate('Something went wrong. Please try again.'),
        status: 'error',
        duration: 2000,
        isClosable: true,
      });
    }
  };

  const handleDownVote = async () => {
    try {
      if (responseVote === "down") {
        if (feedback === "" || feedback === " ") {
          return
        }
      }
      let body = {
        vote: responseVote,
        transcript: transcript,
        feedback: feedback,
        userEmail: localStorage.getItem('userinfo'),
        message_id: currentMessageId
      }
      await axios.post(FEEDBACK_SERVER_URL, body);
      if (currentMessageId) setVotes((v) => ({ ...v, [currentMessageId]: responseVote }));

      setVoteFeedbackSubmitted(true);
      setTimeout(() => {
        setVoteFeedbackSubmitted(false);
        setFeedback('');
        onClose();
      }, 2000);

    } catch (error) {
      setFeedback('');
      onClose();
      console.error('Error sending feedback:', error);
    }
  }

  const toggleSidebar = () => {
    setSidebarVisible(!sidebarVisible);
  }

  // Sidebar resizing functionality
  const [sidebarWidth, setSidebarWidth] = useState(270);
  const [isResizing, setIsResizing] = useState(false);
  const sidebarRef = useRef(0);
  const resizerOffset = useRef(0);
  const minSidebarWidth = 200;
  const maxSidebarWidth = 400;

  const handleMouseDown = (e) => {
    setIsResizing(true);
    resizerOffset.current = e.clientX - sidebarRef.current.getBoundingClientRect().right;
    e.preventDefault();
  };

  useEffect(() => {
    if (!isResizing) return undefined;
    const handleMouseMove = (e) => {
      const newWidth = e.clientX - sidebarRef.current.getBoundingClientRect().left - resizerOffset.current - 30;
      if (newWidth >= minSidebarWidth && newWidth <= maxSidebarWidth) {
        setSidebarWidth(newWidth);
      }
    };
    const handleMouseUp = () => setIsResizing(false);
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing]);

  const welcome = loadingConversation ? <ConversationSkeleton /> : (
    <div className="welcome-screen">
      <div className="welcome-content">
        <img src={botImgDTC} alt="DTC Chatbot" className="welcome-bot-image" />
        <h1 className="welcome-title">{translate("WelcomeDTC")}
          <img src={redVector} alt="vector" />
        </h1>

        <p className="welcome-subtitle"> {translate("AI Subheading")} </p>
      </div>
    </div>
  );

  const footerControls = (
    <>
      <ResponseModeToggle
        responseMode={responseMode}
        setResponseMode={setResponseMode}
        translate={translate}
      />
      <ModelDropDown
        selectedModel={selectedModel}
        setSelectedModel={(value) => {
          setSelectedModel(value);
          setSelectedYear('');  // Reset year when model changes
        }}
        translate={translate}
        langOpt={langOpt}
      />
      <YearDropDown
        selectedYear={selectedYear}
        setSelectedYear={setSelectedYear}
        availableYears={yearsByModel[selectedModel] || []}
        translate={translate}
        disabled={!selectedModel}
      />
    </>
  );

  return (
    <>
      <ChakraProvider resetCSS={false}>
        <Modal isCentered isOpen={isOpen} onClose={onClose}>
          {overlay}
          <ModalContent className="custom-modal-content">
            <ModalCloseButton />
            {!voteFeedbackSubmitted ? (
              <>
                <ModalBody>
                  <h2 className="feedback-title">{translate("Help us shape a better experience by sharing your feedback!")}</h2>
                  <Textarea className='feedInputBox' placeholder={translate("Enter your feedback")} onChange={handleFeedbackChange}></Textarea>
                </ModalBody>
                <ModalFooter>
                  <Button className='closeBtnDTC' onClick={onClose} mr={3} >{translate('Close')}</Button>
                  <Button
                    className={feedback.trim() ? 'submitBtnDTC active' : 'submitBtnDTC'}
                    colorScheme='blue'
                    onClick={() => handleDownVote()}
                    isDisabled={!feedback.trim()}
                  >
                    {translate('Submit')}
                  </Button>
                </ModalFooter>
              </>
            ) : (
              <ModalBody className="feedback-success">
                <div className="success-icon">
                  <svg width="64" height="64" viewBox="0 0 64 64" fill="none">
                    <circle cx="32" cy="32" r="32" fill="#00AA44" />
                    <path d="M20 32L28 40L44 24" stroke="white" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
                <h2 className="success-title">{translate('Thank You!')}</h2>
                <p className="success-message">{translate('Your feedback was successfully submitted.')}</p>
              </ModalBody>
            )}
          </ModalContent>
        </Modal>
        <Modal isCentered isOpen={isFeedbackOpen} onClose={onFeedbackClose}>
          {overlay}
          <ModalContent className="custom-modal-content">
            <ModalCloseButton />
            {!feedbackSubmitted ? (
              <>
                <ModalBody>
                  <h2 className="feedback-title">{translate("Help us shape a better experience by sharing your feedback!")}</h2>
                  <StarRating rating={feedbackRating} setRating={setFeedbackRating} />
                  <Textarea className='feedInputBox' placeholder={translate("Enter your feedback")} onChange={handleFeedbackTextChange} value={feedbackText}></Textarea>
                </ModalBody>
                <ModalFooter>
                  <Button className='closeBtnDTC' onClick={onFeedbackClose} mr={3}>{translate('Close')}</Button>
                  <Button
                    className={feedbackText.trim() ? 'submitBtnDTC active' : 'submitBtnDTC'}
                    colorScheme='blue'
                    onClick={handleSidebarFeedbackSubmit}
                    isDisabled={!feedbackText.trim()}
                  >
                    {translate('Submit')}
                  </Button>
                </ModalFooter>
              </>
            ) : (
              <ModalBody className="feedback-success">
                <div className="success-icon">
                  <svg width="64" height="64" viewBox="0 0 64 64" fill="none">
                    <circle cx="32" cy="32" r="32" fill="#00AA44" />
                    <path d="M20 32L28 40L44 24" stroke="white" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
                <h2 className="success-title">{translate('Thank You!')}</h2>
                <p className="success-message">{translate('Your feedback was successfully submitted.')}</p>
              </ModalBody>
            )}
          </ModalContent>
        </Modal>
      </ChakraProvider>
      <div className="App scroll-div">
        <header>
          {!sidebarVisible && (
            <button className="mobile-menu-btn" onClick={toggleSidebar}>
              <img src={chevronRight} alt="Open Menu" />
            </button>
          )}
          <img src={logo} alt="Fuso Logo" className="fuso-logo" />
          <div className="header-toolbar">
            {/* tools | preferences | account */}
            <MemoryGraphButton conversationId={conversationId} onOpenConversation={openConversation} translate={translate} />
            <AdminButton translate={translate} />
            <span className="header-divider" aria-hidden="true" />
            <ThemeToggle translate={translate} />
            <LanguageDropDown setLanguage={setLangOpt} translate={translate}></LanguageDropDown>
            <ProfilePhoto translate={translate} />
          </div>
        </header>
        <div className={`content ${langOpt === "English" ? 'jakartaFont' : 'japaneseFont'}`}>
          {sidebarVisible && <div className="sidebar-overlay" onClick={toggleSidebar}></div>}
          <aside className={`sidebar ${sidebarVisible ? '' : 'sidebar-collapsed'}`}
            ref={sidebarRef}
            style={{ width: sidebarVisible ? `${sidebarWidth}px` : '60px' }}>
            {sidebarVisible ? (
              <>
                <div className="sidebar-tools">
                  <div className="chat-bot" onClick={toggleSidebar}>
                    <img className="bot-image" src="./bot1.svg" alt="botImage"></img>
                    <h1>DTC Chatbot</h1>
                  </div>
                  <div className="sidebar-tool-button">
                    <button className='start-new-btn' style={{ fontSize: '14px', fontWeight: '400' }}
                      onClick={() => handleNewChat()}>
                      <img src={NewChat} alt="newChatIcon" ></img>
                      {translate("New chat")}
                    </button>
                    <FeedbackLink translate={translate} langOpt={langOpt} />
                    <KnowledgeButton onAsk={askWithVehicle} selectedModel={selectedModel} selectedYear={selectedYear} translate={translate} />
                    <MyFilesButton translate={translate} />
                  </div>
                  <UserHistory
                    openConversation={openConversation}
                    selectedChatId={conversationId}
                    setChildFunc={setChildFunc}
                    setRemoveChatFunc={setRemoveChatFunc}
                    translate={translate}
                    langOpt={langOpt}
                    handleNewChat={() => handleNewChat()}
                    toggleSidebar={toggleSidebar}
                  />
                </div>
                <Tooltip label={translate("Close Sidebar")} className="tooltip">
                  <button className="toggle-sidebar" onClick={toggleSidebar}>
                    <img src={toggleBtn} alt="Collapse Sidebar" />
                  </button>
                </Tooltip>
              </>
            ) : (
              <div className="sidebar-collapsed-content">
                <div className="sidebar-icon">
                  <img src="./bot1.svg" alt="botImage" />
                </div>
                <Tooltip label={translate("New chat")} className="tooltip">
                  <button className="sidebar-icon-btn" onClick={() => handleNewChat()}>
                    <img src={NewChat} alt="newChatIcon" />
                  </button>
                </Tooltip>
                <Tooltip label={translate("Submit Feedback")} className="tooltip">
                  <button className="sidebar-icon-btn" onClick={() => {
                    setOverlay(<OverlayTwo />);
                    onFeedbackOpen();
                  }}>
                    <img src="./feedback.svg" alt="feedbackIcon" />
                  </button>
                </Tooltip>
                <Tooltip label={translate("Open Sidebar")} className="tooltip">
                  <button className="toggle-sidebar-open" onClick={toggleSidebar}>
                    <img src={chevronRight} alt="Expand Sidebar" />
                  </button>
                </Tooltip>
              </div>
            )}
          </aside>
          {sidebarVisible && (
            <div
              className="resizer"
              onMouseDown={handleMouseDown}
              style={{ cursor: 'col-resize', width: '1px', height: '100%', backgroundColor: '#e2e8f0' }}
            ></div>
          )}
          <main className={`main cv-host ${sidebarVisible ? '' : 'full-width'}`}>
            <ChatView
              chat={chat}
              t={translate}
              langOpt={langOpt}
              question={question}
              setQuestion={setQuestion}
              onSend={handleSendMessage}
              onNewChat={handleNewChat}
              onVote={handleVote}
              votes={votes}
              controls={footerControls}
              emptyState={welcome}
              history={{ ...historyPaging, loadOlder }}
            />
          </main>
        </div>
      </div>

      {/* Product Tour - PC Only */}
      {!/Android|webOS|iPhone|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) && (
        <ProductTour translate={(text) => {
          const isDebug = process.env.REACT_APP_DEBUG === 'true';

          if (!isDebug) {
            if (!text || !translations[text]) return text;
            return translations[text].translationJp || text;
          }

          return translate(text);
        }} />
      )}
    </>
  );
}

export default App;
